import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  getAuth,
  PhoneAuthProvider,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  updatePhoneNumber,
} from "firebase/auth";
import { db } from "./register/firebase";
import { doc, setDoc, collection, updateDoc } from "firebase/firestore";

import "./Signup.css";
import logogif from "../images/logo-progress.gif";
import successGif from "../images/logo-long.jpg";

const Signup = () => {
  const navigate = useNavigate();
  const auth = getAuth();

  const [signupMethod, setSignupMethod] = useState("email");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState();
  const [verificationId, setVerificationId] = useState("");
  const [forceResendingToken, setForceResendingToken] = useState(null);
  const [timeLeft, setTimeLeft] = useState(60000);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [userCredential, setUserCredential] = useState();

  useEffect(() => {
    let timer;
    if (isTimerRunning) {
      timer = setInterval(() => {
        setTimeLeft((prevTimeLeft) => {
          if (prevTimeLeft <= 0) {
            clearInterval(timer);
            setIsTimerRunning(false);
            return 0;
          }
          return prevTimeLeft - 1000;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isTimerRunning]);

  const startTimer = () => {
    setTimeLeft(60000);
    setIsTimerRunning(true);
  };

  const onCaptchVerify = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: (response) => {},
          "expired-callback": () => {},
        }
      );
    }
    handleSendOtp();
  };

  const handleSendOtp = () => {
    setLoading(true);
    const formatPh = "+91" + phoneNumber;
    const appVerifier = window.recaptchaVerifier;

    signInWithPhoneNumber(auth, formatPh, appVerifier)
      .then((confirmationResult) => {
        setConfirmationResult(confirmationResult);
        setVerificationId(confirmationResult.verificationId);
        setForceResendingToken(confirmationResult.forceResendingToken);
        setShowOtpInput(true);
        saveUserDetailsToFirestore();
        setLoading(false);
        setOpenDialog(false);
      })
      .catch((error) => {
        setLoading(false);
        setOpenDialog(false);
        alert(`Verification failed: ${error.message}`);
      });
  };

  const handleVerifyOtp = () => {
    setLoading(true);
    setOpenDialog(true);
    const credential = PhoneAuthProvider.credential(verificationId, otp);

    const user = auth.currentUser;
    if (user) {
      updatePhoneNumber(user, credential)
        .then(() => {
          updatePhoneNumberDetailsFirestore(true);
          navigate(`/pricing?uid=${userCredential.user.uid}`);
          setLoading(false);
          setOpenDialog(false);
        })
        .catch((error) => {
          setLoading(false);
          setOpenDialog(false);
          alert(`Verification failed: ${error.message}`);
        });
    }
  };

  const handleEmailPasswordSignup = async () => {
    setLoading(true);
    setOpenDialog(true);
    try {
      const userCredentialauth = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      setUserCredential(userCredentialauth);
      onCaptchVerify();
    } catch (error) {
      setLoading(false);
      setOpenDialog(false);
      if (error.message.includes("auth/email-already-in-use")) {
        navigate("/login");
      } else {
        alert("Signup failed: " + error.message);
      }
    }
  };

  const handleSignup = () => {
    if (signupMethod === "email") {
      handleEmailPasswordSignup();
    }
  };

  const saveUserDetailsToFirestore = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const userDoc = doc(db, "users", user.uid);
        await setDoc(userDoc, {
          name: name,
          phoneNumber: phoneNumber,
          isPhoneVerified: false,
          email: email,
          isValid: false,
          plan_type: "payment_pending",
        });

        const brokerDoc = doc(
          collection(db, "users", user.uid, "brokers"),
          "NEO"
        );
        await setDoc(brokerDoc, { broker_type: "Kotak Neo" });

        const mockBrokerDoc = doc(
          collection(db, "users", user.uid, "brokers"),
          "MOCK_BROKER"
        );
        await setDoc(mockBrokerDoc, {
          broker_type: "Mock_Broker",
          broker_metadata: {},
        });
      }
    } catch (error) {
      console.error("Error saving user details", error);
    }
  };

  const updatePhoneNumberDetailsFirestore = async (isPhoneVerified) => {
    try {
      const user = auth.currentUser;
      if (user) {
        const userDoc = doc(db, "users", user.uid);
        await updateDoc(userDoc, {
          isPhoneVerified: isPhoneVerified,
          phoneNumber: phoneNumber,
        });
      }
    } catch (error) {
      alert(error.message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-150 p-4 pt-16">
      <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-2xl">
        <h2 className="text-3xl font-bold text-center text-[#2d3191] mb-6">
          Sign Up
        </h2>

        <input
          type="text"
          placeholder="Name"
          className="w-full mb-4 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3191]"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <input
          type="email"
          placeholder="Email"
          className="w-full mb-4 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3191]"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <input
          type="password"
          placeholder="Password"
          className="w-full mb-4 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3191]"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <input
          type="tel"
          placeholder="Phone Number"
          className="w-full mb-4 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3191]"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
        />

        {!showOtpInput && (
          <button
            onClick={handleSignup}
            className="w-full bg-green-700 text-white py-3 rounded-lg font-semibold hover:bg-green-800 transition"
          >
            Send OTP
          </button>
        )}

        {showOtpInput && (
          <div>
            <input
              type="text"
              placeholder="Enter OTP"
              className="w-full mt-4 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3191]"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
            <button
              onClick={handleVerifyOtp}
              className="w-full bg-[#2d3191] text-white mt-4 py-3 rounded-lg font-semibold hover:bg-indigo-900 transition"
              disabled={loading}
            >
              Verify OTP
            </button>
            <p
              onClick={handleSendOtp}
              className="text-green-700 text-center mt-3 cursor-pointer hover:underline"
            >
              Resend OTP
            </p>
          </div>
        )}

        <div id="recaptcha-container"></div>

        <div className="text-center mt-6 text-gray-600">
          <p>Already have an account?</p>
          <a
            href="/login"
            className="text-green-700 font-semibold hover:underline"
          >
            Login
          </a>
        </div>

        {openDialog && (
          <div className="flex flex-col items-center justify-center mt-6">
            <img src={logogif} alt="Loading" className="w-16 h-16 mb-2" />
            <p className="text-[#2d3191] font-semibold">Processing...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Signup;
